import type { Translation } from '@/types/translation';

// Portuguese (Brazil) translation file.

const ptTranslation: Translation = {
  common: {
    save: 'Salvar',
    close: 'Fechar',
    search: 'Pesquisar',
    back: 'Voltar',
    cancel: 'Cancelar',
    continue: 'Continuar',
    submit: 'Enviar',
    confirm: 'Confirmar',
    delete: 'Excluir',
    save_changes: 'Salvar alterações',
    loading: 'Carregando...',
  },
  pages: {
    account: {
      tabs: {
        dashboard: 'Painel',
        fleet_overview: 'Visão geral da frota',
        unit_overview: 'Todas as unidades',
        campaigns: 'Campanhas',
        members: 'Membros',
      },
      dashboard: {
        active_fault_codes: {
          title: 'Códigos de Falha Ativos',
          units_title: 'Unidades com códigos de falha ativos',
        },
        connectivity: {
          title: 'Conectividade',
          units_title: 'Status de conectividade',
        },
        fuel_consumption: {
          title: 'Consumo de Combustível',
          details_title: 'Detalhes do Consumo de Combustível',
        },
      },
      fleet_overview: {
        pinned_fleets: 'Frotas fixadas',
        other_fleets: 'Outras frotas',
        pin: 'Fixar frota',
        unpin: 'Desafixar frota',
        edit: 'Editar frota',
        remove: 'Remover frota',
      },
      unit_overview: {
        title: 'Unidades',
        unverified_ownership: 'Propriedade não verificada',
        add_to_fleet: 'Adicionar à frota',
      },
      campaigns: {
        active_table: {
          title: 'Campanhas ativas',
          download_campaigns: 'Baixar campanhas (xlsx)',
          empty_state: {
            title: 'Você não tem campanhas ativas para esta unidade',
          },
        },
        performed_table: {
          title: 'Campanhas realizadas',
          empty_state: {
            title: 'Nenhuma campanha realizada disponível',
          },
        },
      },
      members: {
        title: 'Membros',
        remove_members_from_account: 'Remover {{selectedRows}} membros da conta',
      },
    },
    fleet: {
      tabs: {
        dashboard: 'Painel',
        unit_overview: 'Todas as unidades',
        campaigns: 'Campanhas',
      },
      campaigns: {
        title: 'Campanhas ativas',
        download_campaigns: 'Baixar campanhas (xlsx)',
        empty_state: {
          title: 'Você não tem campanhas ativas para esta frota',
        },
      },
    },
    unit: {
      tabs: {
        specifications: 'Especificações',
        campaigns: 'Campanhas',
        coverage: 'Garantia e Cobertura',
        maintenance_history: 'Histórico de manutenção',
        manuals_handbooks: 'Manuais e Guias',
        diagnostics_log: 'Registro de diagnósticos',
        services: 'Serviços Digitais',
        settings: 'Configurações',
      },
      spare_parts: 'Peças de reposição',
      manuals_selection_description: 'Para qual produto você deseja encontrar manuais e guias.',
      specifications: {
        title: 'Especificações',
      },
      campaigns: {
        active_table: {
          title: 'Campanhas ativas',
          empty_state: {
            title: 'Você não tem campanhas ativas para esta unidade',
          },
        },
        performed_table: {
          title: 'Campanhas realizadas',
          empty_state: {
            title: 'Nenhuma campanha realizada disponível',
          },
        },
      },
      coverage: {
        cta: {
          heading: 'Informações sobre garantia e cobertura',
          message: 'Para dúvidas sobre garantia e cobertura, entre em contato com seu revendedor Volvo Penta ou OEM.',
          find_dealer: 'Encontre o revendedor mais próximo',
          learn_more: 'Saiba mais',
        },
        empty_state: {
          title: 'Nenhuma garantia e/ou cobertura foi encontrada para esta unidade',
        },
      },
      publications: {
        select_menu: {
          category: 'Categoria',
          designation: 'Designação',
          product_type: 'Tipo de produto',
          language: 'Idioma',
        },
        empty_state: {
          title: 'Nenhuma publicação disponível para esta unidade',
        },
      },
      services: {
        title: 'Serviços Disponíveis',
        all_services: {
          title: 'Ativar todos os serviços',
        },
        list: {
          ActiveMonitoring: {
            Marine: {
              title: 'Extended',
              description:
                'Obtenha todos os benefícios do Core, além de alertas automáticos por e-mail enviados diretamente ao concessionário selecionado.',
              checklist: [
                'Alerte seu concessionário instantaneamente se o sistema detectar um código de falha crítico',
                'Permita que seu concessionário prepare ações corretivas imediatas',
              ],
            },
            Industrial: {
              title: 'Monitoramento ativo',
              description:
                'O Active Monitoring ajuda os clientes a manter seus produtos Volvo Penta em operação, detectando problemas precocemente, proporcionando um serviço mais rápido e oferecendo informações claras sobre a integridade e o uso do produto.\n\nO Active Monitoring também habilita o Remote Software Updates, um serviço para instalar campanhas de software over-the-air quando for mais conveniente para você.',
              checklist: [
                'Maior tempo de atividade com alertas antecipados e suporte proativo',
                'Serviço mais rápido e mais bem preparado, com as informações certas disponíveis com antecedência',
              ],
            },
          },
          ActiveMonitoringCore: {
            Marine: {
              title: 'Core',
              description:
                'Compartilhe dados básicos do veículo com seu concessionário para obter suporte mais rápido durante a manutenção ou em caso de problemas inesperados.',
              checklist: [
                'Permita que seu concessionário monitore a integridade do produto e acompanhe as horas de operação',
                'Permita que seu concessionário analise códigos de falha instantaneamente para um planejamento de serviço proativo',
              ],
            },
            Industrial: {
              title: 'Connected Services Core',
              description:
                'O Core permite que o revendedor Volvo Penta monitore o produto Volvo Penta. <ActiveMonitoringCore>Para mais detalhes, consulte a Descrição do Serviço Connected Services Core.</ActiveMonitoringCore>',
              checklist: [],
            },
          },
          ActiveMonitoringEcWifi: {
            Marine: {
              title: 'Extended',
              description:
                'Permita que seu concessionário acompanhe os dados do veículo e receba alertas instantâneos sobre códigos de falha críticos para preparar ações corretivas imediatas.',
              checklist: [
                'Alerte seu concessionário instantaneamente se o sistema detectar um código de falha crítico',
                'Permita que seu concessionário prepare ações corretivas imediatas',
              ],
            },
            Industrial: {
              title: 'Extended',
              description:
                'Permita que seu concessionário acompanhe os dados do veículo e receba alertas instantâneos sobre códigos de falha críticos para preparar ações corretivas imediatas.',
              checklist: [
                'Alerte seu concessionário instantaneamente se o sistema detectar um código de falha crítico',
                'Permita que seu concessionário prepare ações corretivas imediatas',
              ],
            },
          },
          Penta_Position: {
            Marine: {
              title: 'Serviços de localização',
              description:
                'O Location Services ajuda os clientes a localizar e gerenciar rapidamente seus produtos Volvo Penta conectados por meio de uma visualização de mapa no Volvo Penta Connect, facilitando o gerenciamento de serviços e a supervisão da frota.',
              checklist: [
                'Melhor visibilidade da frota com um mapa mostrando a localização mais recente das unidades conectadas',
                'Resposta de serviço mais rápida com fácil acesso à localização da unidade, incluindo horas de operação e códigos de falha',
              ],
            },
            Industrial: {
              title: 'Serviços de localização',
              description:
                'O Location Services ajuda os clientes a localizar e gerenciar rapidamente seus produtos Volvo Penta conectados por meio de uma visualização de mapa no Volvo Penta Connect, facilitando o gerenciamento de serviços e a supervisão da frota.',
              checklist: [
                'Melhor visibilidade da frota com um mapa mostrando a localização mais recente das unidades conectadas',
                'Resposta de serviço mais rápida com fácil acesso à localização da unidade, incluindo horas de operação e códigos de falha',
              ],
            },
          },
        },
        request_sent: 'Solicitação enviada',
        empty_state: {
          title: 'Provisionamento de serviço não disponível',
          message: 'O provisionamento de serviços digitais não está disponível para esta unidade.',
        },
        active_services_title: 'Serviços ativos',
        available_services_title: 'Serviços disponíveis',
        activate_button: 'Ativar serviço',
        terminate_button: 'Encerrar',
        read_more_button: 'Leia mais',
        requires_prefix: 'Requer {{serviceName}}',
      },
      settings: {
        unit_settings: {
          title: 'Configurações da unidade',
          unit_name: 'Nome da unidade',
          home_port: 'Porto base',
          operating_location: 'Local de operação',
          upload_image: 'Enviar imagem',
          add_files: 'Adicionar arquivos',
          helperText: 'Arquivo Png ou .jpg de no máximo 3 MB',
        },
        network_settings: {
          title: 'Configurações de rede',
          empty_state: {
            title: 'Você não tem redes ativas para esta unidade',
            message: 'As configurações de rede para esta unidade aparecerão aqui assim que forem configuradas.',
          },
          updating_title: 'Atualizando configurações de rede',
          updating_description: 'Aguarde enquanto buscamos a configuração de rede mais recente.',
        },
      },
      maintenance: {
        service_protocol_for_chassi: 'Protocolo de serviço para o chassi {{chassisNumber}}',
        download_pdf: 'Baixar PDF',
        service: 'Serviço {{type}}',
        default_service: 'Transmissão {{chassisSeries}} {{chassisNumber}}',
        general_comment: 'Comentário geral do serviço',
        no_comment: 'Nenhum comentário geral para este protocolo de serviço.',
        periodic_tag: 'A cada {{months}} meses / {{engineHours}} h',
        non_periodic_month_tag: 'Aos {{months}} meses',
        non_periodic_hours_tag: 'Às {{engineHours}} h',
        empty_state: {
          title: 'Nenhum histórico de manutenção disponível',
          message:
            'Os protocolos de serviço deste motor aparecerão aqui após serem registrados por um Revendedor Volvo Penta.',
        },
        unverified_ownership_notice:
          'Algumas informações podem ser limitadas porque a propriedade desta unidade ainda não foi verificada. Entre em contato com o seu revendedor Volvo Penta autorizado para concluir a verificação.',
      },
    },
    settings: {
      application_settings: 'Configurações do aplicativo',
      account_name: 'Nome da conta da frota',
      notifications: 'Notificações',
      email: 'E-mail',
      email_notifications: 'Ativar notificações por e-mail',
      sms: 'SMS',
      sms_notifications: 'Ativar notificações por SMS',
    },
    support: {
      title: 'Perguntas frequentes',
      description: 'Encontre informações de contato e informações gerais sobre o Volvo Penta Connect.',
      professional_support: {
        tag: 'Suporte profissional',
        title: 'Os maiores especialistas — a seu serviço',
        description:
          'Entre em contato com um revendedor autorizado Volvo Penta como primeiro ponto de apoio quando precisar de ajuda com serviço de motor, reparos, peças ou outro suporte — para embarcações de lazer e comerciais.',
      },
      emergency_support: {
        tag: 'Suporte de emergência',
        title: 'Volvo Penta Action Service',
        description:
          'Uma pane no motor raramente acontece, mas caso ocorra, o Action Service está disponível para colocá-lo em contato com o parceiro Volvo Penta mais próximo. Oferecemos suporte em vários idiomas, 24 horas por dia, durante todo o ano.',
      },
      platform_support: {
        tag: 'Suporte da plataforma',
        title: 'Suporte Volvo Penta Connect',
        description:
          'Entre em contato com nossa equipe dedicada para obter ajuda com sua conta e os recursos do Volvo Penta Connect. Estamos aqui para ajudá-lo a gerenciar seus dados de motor e conectividade com facilidade.',
      },
      getting_started: {
        title: 'Primeiros passos',
        question_1: {
          title: 'Para quem é o Volvo Penta Connect?',
          body: 'O Volvo Penta Connect é um portal do cliente que oferece uma visão geral das suas unidades e acesso a informações relevantes, como manuais e guias, campanhas, status de garantia e muito mais.',
        },
        question_2: {
          title: 'O que preciso fazer primeiro para usar este produto?',
          body: 'Você precisa registrar uma unidade em sua conta. Após adicionar uma unidade, você poderá monitorar os dados da unidade, instalar atualizações de software over-the-air e acessar manuais e guias vinculados a essa unidade.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Adicionar unidade' },
        },
        question_3: {
          title: 'Posso usar este produto sem adicionar uma unidade?',
          body: 'Não. O Volvo Penta Connect é desenvolvido em torno das suas unidades. Manuais, dados do motor e serviços digitais estão todos vinculados a uma unidade específica. Se você não possui nem pretende registrar uma unidade, este produto não atenderá às suas necessidades.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Adicionar unidade' },
        },
        question_4: {
          title: 'Como registro meu motor?',
          body: 'Vá para a lista de unidades e selecione Adicionar unidade. Pesquise pelo número de série e preencha e envie o formulário de registro. Alguns motores industriais mais recentes vêm com um código QR pré-instalado no motor. Se disponível, você pode escanear este código para iniciar o processo de registro.',
        },
        question_5: {
          title: 'Como começo a usar minha(s) unidade(s)?',
          body: 'Comece registrando sua unidade no Volvo Penta Connect. Vá para a lista de unidades e selecione Adicionar unidade, depois pesquise pelo número de série da unidade e preencha o formulário de registro.<br /><br />Após o registro da unidade, você terá acesso a informações como manuais, peças de reposição, campanhas, status de garantia e serviços digitais vinculados a essa unidade.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Adicionar unidade' },
        },
        question_6: {
          title: 'O que posso fazer após registrar minha unidade?',
          body: 'Com uma unidade registrada, você pode:<br /><br /><ul><li>- Agrupar unidades em frotas por tipo de veículo ou caso de uso</li><li>- Monitorar o status da unidade</li><li>- Instalar atualizações de software over-the-air</li><li>- Acessar manuais e guias específicos para sua unidade</li></ul>',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Visão geral das unidades' },
        },
        question_7: {
          title: 'Onde posso encontrar manuais e guias?',
          body: 'Manuais e guias ficam disponíveis após a adição de uma unidade à sua conta, para que possamos exibir a documentação correspondente à sua unidade específica.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Adicionar unidade' },
        },
        question_8: {
          title: 'Como adiciono um usuário à minha conta de frota?',
          body: 'Para adicionar um novo usuário, você precisará convidá-lo pela página de Membros.<br /><br /><ul><li>1. Vá para Membros</li><li>2. Selecione Adicionar membro</li><li>3. Crie um link de convite e copie-o</li><li>4. Compartilhe o link com a pessoa que deseja convidar</li><li>5. Após a aceitação do convite, atribua uma função ao novo membro</li></ul><br/>Somente usuários com permissão para gerenciar membros podem enviar convites e atribuir funções.',
          link: { href: 'https://www.volvopenta.com/connect/members', label: 'Membros' },
        },
        question_9: {
          title: "Por que minha unidade é marcada como 'Proprietário não verificado'?",
          body: 'Unidades recém-registradas são temporariamente marcadas como Proprietário não verificado até que a propriedade seja confirmada.<br /><br />A propriedade pode ser verificada por um revendedor autorizado Volvo Penta ou pela nossa equipe de suporte. Uma vez verificada, a marcação será removida automaticamente.<br /><br />Se precisar de ajuda com a verificação, entre em contato com seu revendedor autorizado Volvo Penta.',
        },
        question_10: {
          title: 'Por que não consigo ver minhas unidades?',
          body: 'Suas unidades podem não aparecer em sua conta por vários motivos. Verifique os seguintes cenários comuns:<br /><br />Divergência de e-mail:<br/>Suas unidades estão vinculadas a um endereço de e-mail específico. Se você criou uma conta com um endereço diferente, as unidades não serão visíveis. Você pode criar uma nova conta com o e-mail correto ou entrar em contato com um revendedor autorizado Volvo Penta para ajudar a reregistrar sua unidade.<br /><br />Registro pendente:<br/>Se você adicionou uma nova unidade industrial, ela pode ainda não estar em nossos registros. Essas unidades não aparecerão no Volvo Penta Connect até que nossa equipe de suporte conclua o processo de registro manual.<br /><br />Funções de conta:<br/>Seu acesso a unidades específicas depende da sua função atribuída. Se acreditar que seu acesso está restrito, entre em contato com seu gerente de frota para garantir que você tem a função de conta correta.',
        },
      },
      connectivity_telematics: {
        title: 'Conectividade e telemática',
        question_1: {
          title: 'Como sei se minha unidade está conectada?',
          body: 'Você pode ver o status de conectividade de uma unidade ao lado do seu nome na lista de unidades e na página da unidade.<br /><br /><GreenDot /> – A unidade está online. Dados foram recebidos nos últimos 6 minutos.<br /><br /><GreyDot /> – A unidade está offline. Nenhum dado foi recebido nos últimos 6 minutos.<br /><br />Sem indicador – A unidade não possui capacidade de conectividade. Ela pode ser elegível para instalação de um gateway de telemática (TGW) por meio de um revendedor.<br /><br />Se sua unidade estiver offline, ela ainda pode ser usada normalmente, mas as atualizações de dados serão retomadas assim que a unidade se reconectar.',
        },
        question_2: {
          title: 'Por que minha unidade não exibe nenhum status de conectividade?',
          body: 'Unidades sem indicador de status não possuem um gateway de telemática (TGW) instalado ou ativado. Essas unidades podem ser conectadas com a instalação de um TGW por meio de um revendedor Volvo Penta.',
        },
        question_3: {
          title: 'O que é um gateway de telemática (TGW)?',
          body: 'Um gateway de telemática (TGW) é um dispositivo de hardware instalado na unidade que permite a conectividade e a transferência de dados para o Volvo Penta Connect.<br /><br />Sem um TGW, a unidade não pode ser monitorada digitalmente.',
        },
        question_4: {
          title: 'Como conecto minha unidade ao Volvo Penta Connect?',
          body: 'Para conectar uma unidade, as seguintes etapas devem ser concluídas:<br /><br /><ul><li>- A unidade deve ter um TGW instalado</li><li>- O TGW deve ser ativado por um revendedor ou OEM</li><li>- Os serviços digitais necessários devem estar habilitados</li><li>- O proprietário deve aprovar o contrato de gestão de dados</li></ul><br /><br />Após a conclusão, a unidade aparecerá com status de conectividade no Volvo Penta Connect.',
        },
        question_5: {
          title: 'Quais smartphones/tablets são compatíveis com o aplicativo Volvo Penta Connect?',
          body: 'Para dispositivos iOS, a versão do sistema operacional deve ser iOS 17 ou posterior. Para dispositivos Android, a versão do sistema operacional deve ser 9.0 ou posterior.',
        },
        question_6: {
          title: 'Quem pode me ajudar a instalar ou ativar a conectividade?',
          body: 'Um revendedor Volvo Penta pode ajudar com a instalação, ativação do TGW e configuração do serviço.',
          link: {
            href: 'https://www.volvopenta.com/about-us/dealer-locator/',
            label: 'Encontre um revendedor Volvo Penta',
          },
        },
        question_7: {
          title: 'Como configuro o Wi-Fi para minha unidade?',
          body: 'Você pode configurar sua conexão Wi-Fi diretamente no aplicativo seguindo estas etapas:<br /><br /><ul><li>1. Selecione a unidade específica que deseja configurar na sua lista.</li><li>2. Clique na aba Configurações localizada na página da unidade.</li><li>3. Use o Manual de Instruções (PDF) para obter orientações passo a passo para concluir a configuração.</li></ul>',
          link: {
            href: 'https://www.volvopenta.com/connect/documents/Connect-to-WiFi-onboard.pdf',
            label: 'Manual de instruções',
            icon: 'import',
          },
        },
      },
      campaigns_software_updates: {
        title: 'Campanhas e atualizações de software',
        question_1: {
          title: 'O que é uma campanha?',
          body: 'Uma campanha é uma ação iniciada pela Volvo Penta para garantir a qualidade do produto. Se uma campanha afetar uma de suas unidades registradas, você será notificado e orientado pelas etapas necessárias.',
          link: { href: '/connect/campaigns', label: 'Visão geral das campanhas' },
        },
        question_2: {
          title: 'O que devo fazer quando tenho uma campanha ativa?',
          body: 'Depende do tipo de campanha:<br /><br /><ul><li>- **Recall / Qualidade / Serviço:** Entre em contato com seu revendedor local Volvo Penta</li><li>- **Instalação / OTA:** Essas campanhas podem ser concluídas remotamente pelo aplicativo Volvo Penta. Baixe o aplicativo e siga as instruções no aplicativo.</li></ul>',
          link: {
            href: 'https://www.volvopenta.com/about-us/dealer-locator/',
            label: 'Encontre seu revendedor',
          },
          ios: { href: '#', label: 'App Store' },
          android: { href: '#', label: 'Google Play' },
        },
        question_3: {
          title: 'O que preciso antes de instalar uma campanha over-the-air (OTA)?',
          body: 'Antes de iniciar uma instalação OTA, certifique-se de que:<br /><br /><ul><li>- Você selecionou a unidade correta</li><li>- O Bluetooth está ativado no seu dispositivo</li><li>- Você está a até 3 metros da unidade</li><li>- Todas as pré-condições necessárias exibidas no produto foram atendidas</li><li>- A unidade permanece ligada e sem interferência durante a instalação</li></ul><br/>Se algum requisito não for atendido, a instalação não será iniciada.',
        },
        question_4: {
          title: 'Estou tendo problemas para instalar uma campanha over-the-air (OTA).',
          body: 'Se a instalação falhar ou não iniciar:<br /><br /><ul><li>- Verifique se o Bluetooth está ativado e se você está próximo à unidade</li><li>- Permaneça a até 3 metros durante toda a instalação</li><li>- Reinicie o aplicativo e tente novamente</li><li>- Verifique novamente se todas as pré-condições da unidade foram atendidas</li></ul><br/>Se o problema persistir, entre em contato com o suporte.',
        },
        question_5: {
          title: 'Onde posso ver minhas instalações over-the-air (OTA) concluídas?',
          body: 'Você pode visualizar as instalações OTA concluídas na página da unidade. Abra a unidade e vá para a aba Campanhas para ver o histórico completo de instalações.',
        },
        question_6: {
          title: 'Uma conectividade ruim pode afetar uma instalação OTA?',
          body: 'Sim. Uma instalação OTA pode falhar se a unidade tiver conectividade celular ou Wi-Fi instável ou ausente. Certifique-se de que a unidade tenha uma conexão confiável e permaneça conectada até que a instalação seja concluída.',
        },
        question_7: {
          title: 'Por que minha unidade precisa estar conectada regularmente?',
          body: 'A conectividade regular ajuda a garantir que sua unidade esteja pronta para atualizações de software. Se uma unidade não estiver conectada por algum tempo, os dados ou preparações necessários podem estar ausentes, o que pode atrasar ou bloquear uma instalação OTA.',
        },
        question_8: {
          title: 'O que acontece se eu não concluir uma campanha a tempo?',
          body: 'As campanhas não são instaladas automaticamente. Se uma campanha não for concluída dentro do prazo especificado, pode não ser mais possível realizá-la. Siga sempre as instruções exibidas e conclua a campanha dentro do período indicado.',
        },
      },
      maintenance_service: {
        title: 'Manutenção e serviço',
        question_1: {
          title: 'Posso ver o histórico de manutenção da minha unidade?',
          body: 'Sim. Cada unidade possui uma aba Histórico de manutenção onde os serviços concluídos são listados. Somente serviços realizados por revendedores autorizados Volvo Penta são exibidos. Se algum serviço estiver ausente, entre em contato diretamente com seu revendedor.<br /><br />Selecione uma unidade e abra a aba Histórico de manutenção para visualizar os detalhes.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Visão geral das unidades' },
        },
        question_2: {
          title: 'Por que não consigo ver qual revendedor realizou um serviço?',
          body: 'Os detalhes do revendedor são exibidos apenas para proprietários verificados da unidade.<br /><br />Para visualizar o nome e a localização do revendedor de um serviço concluído, você precisa estar registrado como proprietário. Se ainda não foi verificado, entre em contato com seu revendedor autorizado Volvo Penta para confirmar sua propriedade.',
        },
      },
      warranty_coverage: {
        title: 'Garantia e cobertura estendida',
        question_1: {
          title: 'Como sei se minha(s) unidade(s) têm garantia e cobertura?',
          body: 'Cada unidade possui uma aba Garantia e cobertura que exibe as garantias ativas. Selecione uma unidade e abra a aba para ver os detalhes.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Visão geral das unidades' },
        },
        question_2: {
          title: 'O que é a garantia padrão?',
          body: 'Todos os produtos Volvo Penta incluem uma garantia padrão de fábrica. A cobertura se aplica por um período de tempo definido ou por um número de horas de operação—o que ocorrer primeiro—e depende do produto e de sua aplicação.',
        },
        question_3: {
          title: 'O que é a garantia padrão de horas ilimitadas?',
          body: 'Alguns tipos de instalação não têm limite de horas de operação durante o primeiro e/ou segundo ano de cobertura de garantia. Os detalhes dependem do produto e da instalação e são definidos nos termos de garantia.',
        },
        question_4: {
          title: 'O que é a garantia de componentes?',
          body: 'A garantia de componentes cobre componentes principais específicos do seu produto Volvo Penta. A duração da cobertura e os limites de horas de operação variam por componente e são definidos nos termos de garantia.',
        },
        question_5: {
          title: 'O que é a garantia de emissões?',
          body: 'Motores aprovados pelas regulamentações da US EPA ou California ARB incluem cobertura de Garantia de Emissões. Isso se aplica por um período de tempo definido ou por um número de horas de operação, conforme estabelecido nos termos de garantia de emissões.',
        },
      },
      notification: {
        title: 'Notificações',
        question_1: {
          title: 'Por que não estou recebendo notificações sobre minha(s) unidade(s)?',
          body: 'Verifique suas configurações de notificação. Certifique-se de que as notificações estão ativadas na página de Configurações.',
          link: { href: 'https://www.volvopenta.com/connect/settings', label: 'Configurações' },
        },
      },
    },
    login: {
      hero_title: 'Bem-vindo ao Volvo Penta Connect',
      hero_body:
        'Tudo sobre seu motor ou produto Volvo Penta em um só lugar. Manuais, guias, peças de reposição, recalls, campanhas de qualidade, status de garantia e muito mais.',
      log_in: 'Entrar',
      blurb_back_button: 'Início',
      blurb_bolded_main_text:
        'Bem-vindo ao Volvo Penta Connect, a nova plataforma para proprietários de frotas e de embarcações de lazer.',
      blurb_regular_main_text:
        'Registre seu motor para acessar informações relevantes como manuais, recalls e status de garantia. O registro garante que seu revendedor local esteja pronto para apoiá-lo com as peças e serviços certos. E o melhor: é completamente gratuito.',
      CTAs: {
        manuals_label: 'Acesso fácil a manuais',
        manuals_title: 'Todos os seus manuais em um clique',
        manuals_body:
          'Com o Volvo Penta Connect, você não precisa mais pesquisar o número de série para acessar manuais. Nossa plataforma oferece uma solução com um clique, fornecendo a coleção completa de manuais e guias para todos os seus motores e produtos.',
        efficiency_label: 'Eficiência',
        efficiency_title: 'Encontre rapidamente as peças de reposição certas',
        efficiency_body:
          'O Volvo Penta Connect oferece um recurso fácil de usar que simplifica o processo de encontrar a peça de reposição correta para o seu motor Volvo Penta — sem a necessidade de pesquisar o número de série ou o modelo do motor.',
        service_label: 'Acompanhe a manutenção do produto',
        service_title: 'Visualize o histórico de serviços registrado para seus produtos',
        service_body:
          'Mantenha-se atualizado sobre a saúde dos seus produtos com o Volvo Penta Connect. Obtenha uma visão geral abrangente, incluindo detalhes de serviço, do seu histórico de serviços registrado para todos os seus produtos Penta.',
      },
      cards: {
        uptime_label: 'Garanta a disponibilidade',
        uptime_title: 'Descubra se você tem campanhas ativas',
        uptime_body:
          'Com o Volvo Penta Connect, acompanhar as campanhas dos seus produtos nunca foi tão fácil. Mantenha seus produtos atualizados e sempre funcionando no seu melhor.',
        connect_label: 'Acompanhe todas as suas garantias',
        connect_title: 'Suas garantias no Volvo Penta Connect',
        connect_body:
          'Obtenha uma visão geral de todas as suas garantias e coberturas. Verifique se estão ativas e por quanto tempo serão válidas, e receba notificações quando estiverem prestes a expirar.',
      },
    },
    welcome: {
      title: 'Bem-vindo!',
      body: 'Para aproveitar ao máximo o Volvo Penta Connect, você precisa registrar uma embarcação ou máquina em sua conta. Uma vez adicionada, você terá acesso a manuais e guias, acesso mais fácil a peças de reposição relevantes, status de garantia e muito mais.',
      get_help: 'Obter ajuda',
      get_started: 'Começar',
      accordion: {
        who_is_it_for: {
          title: 'Para quem é o Volvo Penta Connect?',
          body: 'O Volvo Penta Connect é um portal para clientes onde você obtém uma visão geral de suas unidades e informações relevantes, como manuais e guias, campanhas, status de garantia e muito mais.',
        },
        how_to_register: {
          title: 'Como posso usar este produto sem registrar uma unidade?',
          body: 'Você precisa registrar uma unidade em sua conta. Uma vez adicionada, você pode monitorar os dados da unidade, instalar atualizações de software over-the-air e acessar manuais e guias vinculados a essa unidade.',
        },
        can_i_use_without_registering: {
          title: 'Onde posso encontrar manuais e guias?',
          body: 'Manuais e guias ficam disponíveis assim que uma unidade é adicionada à sua conta, para que possamos mostrar a documentação que corresponde à sua unidade específica.',
        },
      },
    },
  },
  components: {
    modal: {
      accept_invitation_modal: {
        invite_title: 'Bem-vindo(a) ao {{name}}!',
        invite_description:
          'Você foi convidado(a) para o espaço de trabalho {{fleetName}}. Aqui você pode monitorar a saúde da frota e das unidades, acompanhar análises e receber notificações sobre atualizações de campanhas e garantias.',
        accept: 'Aceitar convite',
        dismiss: 'Dispensar',
      },
      add_units_fleet_modal: {
        button_add: 'Criar frota',
        button_update: 'Atualizar frota',
        title_add: 'Criar frota',
        title_update: 'Atualizar frota',
        title_success: 'Frota criada com sucesso',
        text_success: 'Sua frota foi criada. Se a nova frota não aparecer na lista, atualize a página.',
        fleet_name: 'Digite o nome da frota ou selecione sua frota',
        select_your_fleet: 'Selecione sua frota',
        click_to_create_fleet: 'Clique para criar a frota',
        table: {
          unit_name: 'Nome da unidade',
          location: 'Localização',
          engines: 'Motores',
          fleets: 'Frotas',
        },
        cancel: 'Pular por agora',
        submit: {
          add_units: 'Adicionar unidade(s) à frota',
          create_fleet: 'Criar frota',
          close: 'Fechar',
        },
      },
      remove_units_fleet_modal: {
        button_remove: 'Remover da frota',
        title_remove: 'Remover unidades da frota',
        description: 'Você está prestes a remover as unidades selecionadas da frota. Esta ação não pode ser desfeita.',
        confirmation: 'Tem certeza de que deseja remover as unidades selecionadas da frota?',
        cancel: 'Cancelar',
        submit: {
          create_fleet: 'Remover da frota',
        },
      },
      delete_fleet_modal: {
        title: 'Remover frota',
        description:
          'A remoção de uma frota da sua conta é permanente. Você tem a opção de adicionar novamente uma frota a qualquer momento.',
        confirmation: 'Tem certeza de que deseja remover a seguinte frota?',
        submit: 'Sim, remover frota da conta',
      },
      edit_fleet_modal: {
        title: 'Editar frota',
        fleet_name: 'Nome da frota',
        select_icon: 'Selecionar ícone',
      },
      add_new_unit_modal: {
        button: 'Adicionar unidade',
        search_unit: {
          title: 'Encontre sua unidade',
          description: 'Pesquise a unidade que deseja adicionar usando o número de série do motor.',
          serial_number: 'Número de série do motor',
          find_serial_number: 'Como encontrar seu número de série',
          is_not_found: {
            label: 'Número de série não existe em nossos registros',
            body: 'Certifique-se de ter encontrado o número de série correto. Se ainda não funcionar, entre em contato com seu revendedor Volvo Penta ou encontre um em nosso <a>Localizador de Revendedores</a> para obter ajuda.',
          },
          is_not_registered_industrial: {
            label: 'Unidade não registrada para este produto',
            body: 'Registre a unidade para que ela seja adicionada à sua conta. A solicitação de registro será tratada pela nossa equipe de suporte e a unidade será adicionada após a conclusão.',
          },
          is_not_registered_marine: {
            label: 'Unidade não registrada para este produto',
            body: 'A unidade precisa ser registrada para que você possa adicioná-la à sua conta. Entre em contato com seu revendedor Volvo Penta ou encontre um em nosso <a>Localizador de Revendedores</a> para obter ajuda com o registro da unidade.',
          },
          is_maximum_units_reached: {
            label: 'Número máximo de unidades atingido',
            body: 'Você pode adicionar até três unidades não próprias. Para adicionar mais, você precisa estar registrado como proprietário da unidade. Entre em contato com seu revendedor Volvo Penta para obter ajuda, ou use nosso Localizador de Revendedores para encontrar um próximo a você.',
          },
          is_unit_already_added: {
            label: 'Unidade já adicionada à sua conta',
            body: 'Esta unidade já está disponível em sua conta e não precisa ser registrada novamente. Você pode encontrá-la na sua lista de unidades.',
          },
        },
        unit_information: {
          title: 'Inserir informações da unidade',
          description:
            'Preencha os detalhes abaixo para adicionar sua embarcação ao Volvo Penta Connect e receber informações relevantes sobre seu Produto Penta.',
          privacy_information:
            'A Volvo Penta registra e utiliza determinados dados pessoais sobre você. Se tiver interesse em saber mais sobre como a Volvo Penta trata seus dados pessoais, acesse <a>Informações de privacidade para registro de proprietário ou operador</a>.',
        },
        marine_unit_information: {
          serial_number: 'Número de série do motor',
          serial_number_helperText: 'O número de série Volvo Penta está na placa do motor.',
          vessel_name: 'Nome da embarcação',
          vessel_name_helperText: 'O nome (ex.: Black Pearl) dado à sua embarcação.',
          vessel_id: 'ID da embarcação',
          imo_number: 'Número IMO',
          imo_number_helperText:
            'Número da Organização Marítima Internacional que identifica exclusivamente sua embarcação.',
          mmsi_number: 'Número MMSI',
          mmsi_number_helperText: 'Número de Identidade do Serviço Móvel Marítimo usado para comunicação via rádio.',
          manufacturer: 'Fabricante',
          manufacturer_helperText: 'O fabricante da sua embarcação.',
          model: 'Modelo',
          model_helperText: 'O modelo da embarcação.',
          vessel_segment: 'Segmento da embarcação',
          type_of_vessel: 'Tipo de embarcação',
          home_port: 'Porto base',
          home_port_helperText: 'Local ou porto base da sua embarcação',
          add_unit: 'Adicionar unidade',
        },
        marine_unit_added: {
          title: 'Sua unidade foi adicionada',
          description:
            'Sua embarcação foi adicionada e você poderá ver as informações da embarcação, baixar manuais, encontrar peças de reposição e muito mais.',
          add_another_unit: 'Adicionar outra unidade',
          go_to_unit: 'Ir para a unidade',
        },
        industrial_contact_information: {
          title: 'Inserir informações de contato',
          description:
            'Preencha os detalhes do proprietário abaixo para adicionar sua unidade. As informações inseridas serão verificadas manualmente pela nossa equipe de suporte antes de registrá-lo como proprietário.',
          proof_of_ownership: 'Comprovante de propriedade',
          proof_of_ownership_helperText:
            'Um comprovante de propriedade (ex.: contrato, recibo ou confirmação de pedido) é necessário para registrar a empresa como proprietária da máquina. Se não puder fornecer o comprovante, entre em contato com seu revendedor.',
          proof_of_ownership_upload:
            'Arraste e solte ou clique para selecionar arquivos PDF, png, jpg ou heic de no máximo 3 MB cada.',
          proof_of_ownership_qr: 'Propriedade confirmada por código QR',
          contact_person: 'Pessoa de contato',
          contact_person_description: 'Você será registrado como a pessoa de contato da empresa e desta máquina.',
          first_name: 'Nome',
          last_name: 'Sobrenome',
          email: 'E-mail',
          company_details: 'Detalhes da empresa',
          company_details_description: 'Insira as informações da empresa proprietária da máquina.',
          company_name: 'Nome da empresa',
          company_name_helperText: 'A empresa proprietária da máquina.',
          vat_number: 'Número de CNPJ/IVA',
          address_line: 'Endereço',
          zip_code: 'CEP / Código postal',
          city: 'Cidade',
          state: 'Estado / Província',
          country: 'País',
          notification: 'Preferências de notificação',
        },
        industrial_unit_information: {
          engine_serial_number: 'Número de série do motor',
          engine_serial_number_helperText:
            'Se sua máquina tiver mais de um motor, clique para adicionar outro campo e insira o número de série do motor adicional.',
          add_engine: 'Adicionar motor',
          machine_name: 'Nome da máquina',
          machine_name_helperText: 'O nome (ex.: T4, Unidade 11) da sua máquina, veículo ou gerador.',
          machine_id: 'ID da máquina',
          machine_id_helperText:
            'O número de identificação exclusivo fornecido pelo fabricante original do equipamento (OEM).',
          manufacturer: 'Fabricante',
          manufacturer_helperText: 'O fabricante da sua máquina, veículo ou gerador (ex.: Magni, Inmesol, Atlas).',
          model: 'Modelo',
          model_helperText: 'O modelo da sua máquina, veículo ou gerador.',
          machine_segment: 'Segmento da máquina',
          machine_segment_helperText:
            'O segmento em que sua máquina se encontra (ex.: Manuseio de materiais, mineração, silvicultura).',
          type_of_machine: 'Tipo de máquina',
          type_of_machine_helperText: 'Qual o tipo de máquina (ex.: trator de terminal, colhedora, empilhadeira).',
          operating_location: 'Local de operação',
          operating_location_helperText: 'Local ou local de operação da sua máquina.',
        },
        industrial_unit_added: {
          title: 'Unidade adicionada',
          description:
            'Sua máquina foi adicionada e você poderá ver as informações da máquina, baixar manuais, encontrar peças de reposição e muito mais.',
          manual_ownership_registration: 'Registro manual de propriedade',
          manual_ownership_registration_description:
            'O registro e a verificação de propriedade são feitos manualmente pela nossa equipe de suporte. <b>Atualmente enfrentamos alta demanda, o que resulta em longos prazos de processamento, entre 2 e 6 semanas.</b> Você receberá um e-mail de confirmação quando sua solicitação for aprovada.',
          add_another_unit: 'Adicionar outra unidade',
          go_to_unit: 'Ir para a unidade',
        },
        industrial_no_unit_added: {
          title: 'A máquina não aparecerá no Volvo Penta Connect',
          description:
            'Sua máquina não existe em nossos registros. Ela não aparecerá no Volvo Penta Connect até que o processo de registro manual pela nossa equipe de suporte seja concluído.',
          manual_registration: 'Registro manual',
          manual_registration_description:
            'O registro e a verificação de propriedade são feitos manualmente pela nossa equipe de suporte. <b>Atualmente enfrentamos alta demanda, o que resulta em longos prazos de processamento, entre 2 e 6 semanas.</b> Você receberá um e-mail de confirmação quando sua solicitação for aprovada.',
          manuals_campaigns: 'Manuais, peças e campanhas',
          manuals_campaigns_description:
            'Se você está procurando manuais e guias, peças de reposição ou campanhas — aqui estão links onde você pode encontrá-los até que o registro seja concluído. Você também recebeu essas informações na sua caixa de entrada de e-mail.',
          add_another_unit: 'Adicionar outra unidade',
        },
      },
      invite_members_modal: {
        button: 'Adicionar membros',
        title: 'Links de convite pendentes',
        empty_state: {
          title: 'Nenhum convite encontrado.',
          message: 'Tente criar e copiar um convite para sua área de transferência usando o botão abaixo.',
        },
        generate_invitation_link: 'Gerar e copiar novo link de convite',
      },
      remove_members_modal: {
        title: 'Remover membro(s)',
        description:
          'A remoção de um membro da conta {{count}} é permanente. Você tem a opção de reconvidar um membro a qualquer momento.',
        are_you_sure: 'Tem certeza de que deseja remover o(s) seguinte(s) membro(s)?',
        cancel: 'Cancelar',
        submit: 'Sim, remover membro(s) da conta',
      },
      add_network_modal: {
        button: 'Adicionar rede',
        title: 'Adicionar rede',
        refresh_network: 'Atualizar rede',
        edit_network: 'Editar rede',
        remove_network: 'Remover rede',
        remove_network_description:
          'Remova a rede da sua lista salva para este veículo / embarcação. A remoção da rede é permanente. Você pode sempre adicionar a rede novamente.',
        network_name: 'Nome da rede (SSID)',
        network_name_helperText: 'ID da rede do roteador Wi-Fi',
        password: 'Senha',
        password_helperText: 'Senha do roteador Wi-Fi',
        error_title: 'Algo deu errado',
        error_description: 'Não foi possível atualizar a rede. Por favor, tente novamente mais tarde.',
        priority: 'Prioridade',
      },
      wifi_polling_modal: {
        title: 'Atualizando Configurações de Rede',
        title_error: 'Atualização das Configurações de Rede',
        connecting: 'Conectando ao seu dispositivo...',
        searching: 'Buscando suas configurações de rede...',
        almost: 'Quase lá, buscando a configuração...',
        hang_tight: 'Ainda trabalhando nisso, aguarde...',
        communicating: 'Comunicando com o gateway...',
        moment: 'Só mais um momento...',
        timeout: 'Não foi possível alcançar seu dispositivo. Por favor, tente novamente mais tarde.',
        error: 'Algo deu errado ao atualizar as configurações de rede. Por favor, tente novamente.',
      },
      wifi_error_log_modal: {
        title: 'Registro de erros',
        no_errors: 'Nenhum erro encontrado',
        close: 'Fechar',
      },
      digital_services_modal: {
        activate_service_content: {
          title: 'Ativar serviço',
          description:
            'Para nos ajudar a preparar o contrato de ativação correto e solicitar os dados de contato adequados, especifique em nome de quem você está ativando este serviço.',
          myself: 'Eu mesmo',
          myself_helper: 'Ative este serviço para sua máquina ou embarcação pessoal.',
          company: 'Uma empresa',
          company_helper: 'Ative este serviço em nome de uma empresa, frota ou organização.',
        },
        contract_details_content: {
          title: 'Detalhes de contato',
          first_name: 'Nome',
          last_name: 'Sobrenome',
          email_address: 'Endereço de e-mail',
          company_name: 'Nome da empresa',
          company_details_title: 'Dados da empresa',
          company_details_description: 'Insira as informações da empresa proprietária da máquina.',
          vat_number: 'CNPJ',
          social_security_number: 'CPF',
          state_province: 'Estado/Província',
          country: 'País',
          address: 'Endereço',
          city: 'Cidade',
          postal_code: 'CEP / Código postal',
          is_correct_info: 'Confirmo que as informações fornecidas acima são verdadeiras e corretas.',
          is_terms_checked:
            'Reconheço e confirmo que li e compreendi os termos e condições do Contrato de Serviços Digitais (incluindo apêndices) e que estou autorizado a aceitar o referido <a>contrato</a> com efeito juridicamente vinculante.',
          data_management_agreement: 'Também li e compreendi o <a>Contrato de Gestão de Dados</a>.',
          remote_software_updates_title: 'Atualizações Remotas de Software',
          service_description_for: 'Descrição do Serviço para {{serviceName}}',
          is_service_details_checked:
            'Reconheço e confirmo que li e compreendi a <serviceDescription /> e que estou autorizado a aceitar os referidos detalhes do serviço.',
          is_data_act_checked:
            'A Lei de Dados da UE exige que forneçamos informações pré-contratuais descrevendo como um Produto Conectado Volvo Penta (colocado no mercado de um estado-membro da UE) gera dados do produto, como os dados do produto são coletados, como você como usuário pode acessá-los e como são utilizados pela Volvo Penta. As informações completas estão disponíveis em <a>EU Data Act | Volvo Penta</a>. Ao marcar esta caixa e prosseguir com a compra, arrendamento ou aluguel do Produto Conectado Volvo Penta, você reconhece que teve acesso a essas informações pré-contratuais conforme exigido pela Lei de Dados da UE.',
        },
        choose_receiver_content: {
          title: 'Escolha um revendedor',
          description:
            'Adicione um revendedor para receber as notificações de alerta. Isso garante máxima disponibilidade e o planejamento de serviço mais eficiente para seu produto Volvo Penta.',
          find_nearest_dealer: 'Encontre seu revendedor mais próximo',
          dealer: 'Revendedor',
          dealer_placeholder: 'Digite o nome do revendedor e/ou seu endereço de e-mail',
        },
        thanks_content: {
          title: 'Obrigado!',
          description:
            'Observe que a ativação não é imediata e levará algum tempo. Notificaremos você por e-mail quando o serviço estiver pronto para uso.',
          description_all_services: 'Notificaremos você por e-mail quando os serviços estiverem prontos para uso.',
          Marine: {
            description:
              'Observe que a ativação não é imediata e levará algum tempo. Notificaremos você por e-mail quando a ativação for concluída.',
          },
        },
        deactivate_service: {
          title: 'Encerrar serviço',
          description: 'Tem certeza de que deseja encerrar o serviço?',
          description_all: 'Tem certeza de que deseja encerrar todos os serviços ({{serviceNames}})?',
          deactivate: 'Encerrar',
        },
      },
    },
    table: {
      items_selected: '{{selectedRowsCount}} itens selecionados',
      page: 'Página {{currentPage}} de {{totalPages}}',
      unit_table: {
        unit_name: 'Nome da unidade',
        model_brand: 'Modelo/marca',
        location: 'Localização',
        engines: 'Motores',
        operating_hours: 'Horas de operação',
        fleets: 'Frotas',
        campaigns: 'Campanhas',
        digital_services: 'Serviços Digitais',
        unverified_ownership: 'Propriedade não verificada',
      },
      campaigns_table: {
        campaign: 'Campanha',
        download: 'Baixar',
        created_date: 'Data de criação',
        valid_to: 'Válido até',
        product: 'Produto',
        type: 'Tipo',
      },
      members_table: {
        name: 'Nome',
        email: 'E-mail',
        account: 'Conta',
      },
      invitations_table: {
        creation_date: 'Data de criação',
      },
      specifikationer_table: {
        driveline: 'Transmissão',
        product_type: 'Tipo de produto',
        product: 'Produto',
        serial_number: 'Número de série',
        operating_hours: 'Horas de operação',
        engine: 'Motor',
        segment: 'Segmento',
        rated_power: 'Potência nominal',
        rated_speed: 'Velocidade nominal',
      },
      network_settings_table: {
        priority: 'Prioridade',
        ssid: 'SSID',
        frequency: 'Frequência',
        last_updated: 'Última atualização',
        connection: 'Conexão',
        connected: 'Conectado',
        not_connected: 'Não conectado',
        lost_connection: 'Conexão perdida',
        disconnected: 'Desconectado',
      },
      coverage_table: {
        type: 'Tipo',
        description: 'Garantia/cobertura',
        expiration_hours: 'Horas de vencimento',
        expiration_date: 'Data de vencimento',
      },
      fault_codes_table: {
        unit: 'Unidade',
        fault_code: 'Código de falha',
        fault_description: 'Descrição',
        failure_event: 'Evento de falha',
        timestamp: 'Registro de data e hora',
        operating_hours: 'Horas de operação',
        occurrences: 'Ocorrências',
        severity: 'Gravidade',
      },
      connectivity_table: {
        unit: 'Unidade',
        operating_hours: 'Horas de operação',
        last_seen: 'Visto pela última vez',
        status: 'Status de conectividade',
      },
      fuel_consumption_table: {
        unit: 'Unidade',
        chassis_id: 'ID do chassi',
        operating_hours: 'Horas de operação (h)',
        fuel_efficiency: 'Eficiência de combustível (l/h)',
        total_fuel: 'Combustível total (l)',
      },
      publications_table: {
        title: 'Título',
        description: 'Descrição',
        designation: 'Produto',
        product: 'Tipo de produto',
        size: 'Tamanho',
        languageCode: 'Idioma',
        buy: 'Comprar',
      },
      maintenance_dealer_table: {
        header: 'Concessionária',
        dealer_name: 'Nome do revendedor',
        location: 'Localização',
      },
      maintenance_protocol_details_table: {
        header: 'Detalhes do protocolo de serviço',
        title: 'Título',
        current_service: 'Serviço atual',
        previous_service: 'Serviço anterior',
        date: 'Data',
        operating_hours: 'Horas de operação',
        unit_age: 'Idade da unidade',
        months: 'meses',
      },
      maintenance_driveline_table: {
        header: 'Linha de tração {{driveline}}',
        name: 'Nome',
        serial_number: 'Número de série',
        product: 'Produto',
      },
      maintenance_service_table: {
        header: 'Serviço {{service}}',
        activity: 'Atividade',
        detail: 'Detalhe',
        last_performed: 'Última realização',
        performed: 'Realizado',
        complete: 'Concluído',
        not_performed: 'Não realizado',
      },
    },
    tooltip: {
      copy_clipboard: 'Copiar para a área de transferência',
    },
    language_selector: {
      de: 'Sprache - Deutsch',
      en: 'Language - English',
      fr: 'Langue - Français',
      it: 'Lingua - Italiano',
      nl: 'Taal - Nederlands',
      pt: 'Idioma - Português',
      sv: 'Språk - Svenska',
    },
    main_menu: {
      home: 'Início',
      settings: 'Configurações',
      support: 'Suporte',
    },
    user_menu: {
      title: 'Menu do usuário',
      account_settings: 'Configurações da conta',
      logout: 'Sair',
      switch_fleet_account: 'Alternar conta de frota',
    },
    footer: {
      useful: 'Útil',
      manuals_and_handbooks: 'Manuais e guias',
      dealer_locator: 'Localizador de Revendedores',
      warranty: 'Garantia',
      parts_column: 'Peças e Acessórios',
      parts: 'Peças',
      remanufactured: 'Componentes e motores recondicionados',
      contact: 'Contato',
      action_service: 'Volvo Penta Action Service',
      faq: 'Perguntas frequentes',
      contact_us: 'Entre em contato',
      privacy_policy: 'Política de Privacidade',
      cookie_policy: 'Política de Cookies',
    },
    cta: {
      members_fleetaccount_cta: {
        heading: 'Convide outras pessoas para sua conta',
        message:
          'Adicione membros para compartilhar acesso às suas unidades e frotas. Você pode convidar colegas, parceiros de negócios ou familiares e atribuir funções para controlar o que eles podem ver e gerenciar. Copie o link e envie ao seu membro.',
      },
      members_digitalaccount_cta: {
        heading: 'Convide outras pessoas para sua conta',
        message:
          'Convidar membros requer um proprietário de unidade verificado. Após a confirmação da sua propriedade, você poderá convidar colegas, parceiros de negócios ou familiares e atribuir funções para controlar o que eles podem ver e gerenciar.<br /><br />Para verificar a propriedade, entre em contato com seu revendedor autorizado Volvo Penta.',
      },
      action_cta: {
        heading: 'Como agir',
        message: 'Para realizar uma campanha, entre em contato com seu revendedor Volvo Penta ou OEM.',
        find_dealer: 'Encontre seu revendedor mais próximo',
        learn_more: 'Saiba mais',
      },
      notifications_cta: {
        heading: 'Notificações',
        message:
          'Mantenha-se atualizado e receba notificações por e-mail quando uma unidade tiver uma campanha instalável disponível. ',
        notification_settings: 'Configurações de notificação',
      },
    },
    role_types: {
      title: 'Informações sobre funções',
      manager: {
        title: 'Gerente',
        description:
          'Os gerentes têm controle administrativo total para supervisionar a frota, monitorar a saúde das unidades e gerenciar todas as unidades, configurações e acessos de usuários.',
      },
      technician: {
        title: 'Técnico',
        description:
          'Foca na saúde e manutenção da unidade. Os técnicos podem monitorar os dados da unidade e realizar atualizações remotas de software. Esta função não inclui permissões de gerenciamento de frota ou usuários.',
      },
      viewer: {
        title: 'Visualizador',
        description:
          'Os visualizadores têm acesso somente leitura. Eles podem monitorar o status da unidade e visualizar relatórios, mas não podem alterar configurações ou configurações.',
      },
    },
    campaign_types: {
      title: 'Entendendo os tipos de campanha',
      recall: {
        title: 'Recall',
        description:
          'Introdução retroativa de alteração de produção; deve ser implementada o mais cedo possível a fim de garantir o funcionamento adequado do produto.',
      },
      quality: {
        title: 'Qualidade',
        description:
          'Introdução retroativa de alteração de produção; deve ser implementada na próxima manutenção programada a fim de garantir o funcionamento adequado do produto.',
      },
      service: {
        title: 'Serviço',
        description:
          'Introdução retroativa de alteração de produção relacionada à segurança; deve ser implementada imediatamente a fim de garantir a operação segura do produto.',
      },
      installation: {
        title: 'Instalação',
        description:
          'Uma atualização remota de software está pronta para ser instalada em sua unidade usando o Aplicativo Volvo Penta.',
        app_store: 'Baixe para iOS na App Store',
        google_play: 'Baixe para Android no Google Play',
      },
    },
  },
};

export default ptTranslation;
